"use client";

import { AnimatePresence, m } from "motion/react";
import { memo, useMemo } from "react";
import { ANIMATION_CONFIGS } from "@/constants/arcade-styles";

interface FloatingText {
  id: string;
  x: number;
  y: number;
  font: string;
  delay: number;
}

interface FloatingTextsProps {
  texts: FloatingText[];
}

// Pre-calculate animation directions for better performance
const FLOATING_DIRECTIONS = Array.from({ length: 10 }, () => {
  const angle = Math.random() * Math.PI * 2;
  const distance = 60 + Math.random() * 40;
  return {
    x: Math.cos(angle) * distance,
    y: Math.sin(angle) * distance - 30, // Bias slightly upward
    rotate: Math.random() * 60 - 30,
  };
});

// Memoized floating text component
const FloatingTextItem = memo(({ text, index }: { text: FloatingText; index: number }) => {
  // Use pre-calculated direction based on index
  const direction = FLOATING_DIRECTIONS[index % FLOATING_DIRECTIONS.length];

  return (
    <m.div
      className={`pointer-events-none fixed z-[100] ${text.font} text-xs font-bold uppercase text-white`}
      style={{
        left: text.x,
        top: text.y,
        textShadow: `0 0 8px rgba(255,255,255,0.2), 0 2px 4px rgba(0,0,0,0.8)`,
        willChange: "transform, opacity", // GPU acceleration hint
      }}
      initial={{
        opacity: 0,
        scale: 0.5,
        x: 0,
        y: 0,
        rotate: Math.random() * 40 - 20,
      }}
      animate={{
        opacity: ANIMATION_CONFIGS.FLOATING_TEXT.opacity as any,
        scale: ANIMATION_CONFIGS.FLOATING_TEXT.scale as any,
        // Use transform3d for GPU acceleration
        transform: `translate3d(${direction.x}px, ${direction.y}px, 0) rotate(${direction.rotate}deg)`,
      }}
      transition={{
        duration: ANIMATION_CONFIGS.FLOATING_TEXT.duration,
        delay: text.delay,
        ease: "easeOut",
        times: ANIMATION_CONFIGS.FLOATING_TEXT.times as unknown as number[],
      }}
    >
      henlo
    </m.div>
  );
});

FloatingTextItem.displayName = "FloatingTextItem";

// Main floating texts container
export const FloatingTexts = memo(({ texts }: FloatingTextsProps) => {
  // Memoize the texts with stable keys
  const stableTexts = useMemo(
    () =>
      texts.map((text, index) => ({
        ...text,
        stableIndex: index,
      })),
    [texts],
  );

  return (
    <AnimatePresence>
      {stableTexts.map((text, _index) => (
        <FloatingTextItem key={text.id} text={text} index={text.stableIndex} />
      ))}
    </AnimatePresence>
  );
});

FloatingTexts.displayName = "FloatingTexts";
